import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { API_ENDPOINTS } from '../config';

export default function AdminPanel() {
  const { currentUser, firebaseUser } = useAuth();
  const [orders, setOrders] = useState([]);
  const [riders, setRiders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingOrderId, setUpdatingOrderId] = useState(null);

  useEffect(() => {
    if (firebaseUser && currentUser && currentUser.role === 'admin') {
      fetchData();
    }
    // eslint-disable-next-line
  }, [firebaseUser, currentUser]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const token = await firebaseUser.getIdToken();
      const [ordersRes, ridersRes] = await Promise.all([
        axios.get(API_ENDPOINTS.orders, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(API_ENDPOINTS.riders, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setOrders(ordersRes.data);
      setRiders(ridersRes.data);
    } catch (err) {
      setError('Failed to fetch orders or riders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (orderId, newStatus, riderId) => {
    setUpdatingOrderId(orderId);
    try {
      const token = await firebaseUser.getIdToken();
      // Find the rider in the riders list to get their user ID
      const selectedRider = riders.find(r => r._id === riderId);
      if (!selectedRider) {
        setError('Selected rider not found');
        return;
      }
      await axios.patch(
        `${API_ENDPOINTS.orders}/${orderId}/status`,
        { status: newStatus, riderId: selectedRider.user._id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchData();
    } catch (err) {
      setError('Failed to update order status');
    } finally {
      setUpdatingOrderId(null);
    }
  };

  if (!firebaseUser) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00ffe7]"></div>
      </div>
    );
  }

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="max-w-xl mx-auto py-24 text-center">
        <h1 className="text-3xl font-bold mb-4 text-red-600">Unauthorized</h1>
        <p className="text-gray-700">You must be an admin to access this page.</p>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto py-12">
      <h1 className="text-4xl font-extrabold mb-8 text-center text-[#00bfae]">NexGENGaming Admin Panel</h1>
      <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
        <h2 className="text-2xl font-bold mb-4">Orders Management</h2>
        {loading ? (
          <div className="flex justify-center items-center h-32">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#00ffe7]"></div>
          </div>
        ) : error ? (
          <div className="text-red-500 mb-4">{error}</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Order ID</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">User</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Items</th>
                  <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {orders.map((order) => (
                  <tr key={order._id}>
                    <td className="px-4 py-2 font-mono text-xs">{order._id.slice(-6)}</td>
                    <td className="px-4 py-2">{order.user?.name || 'N/A'}<br /><span className="text-xs text-gray-400">{order.user?.email}</span></td>
                    <td className="px-4 py-2">
                      <span className={`px-2 py-1 rounded text-xs font-semibold ${
                        order.status === 'paid' ? 'bg-green-100 text-green-800' :
                        order.status === 'shipped' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'delivered' ? 'bg-purple-100 text-purple-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-4 py-2">
                      <ul className="list-disc ml-4">
                        {order.items.map((item) => (
                          <li key={item._id}>
                            {item.product?.name} ({item.variant.color}, {item.variant.size}) x {item.quantity}
                          </li>
                        ))}
                      </ul>
                    </td>
                    <td className="px-4 py-2">
                      {order.status === 'paid' ? (
                        <div className="flex flex-col gap-2">
                          <select
                            className="border rounded px-2 py-1"
                            defaultValue=""
                            onChange={e => handleStatusChange(order._id, 'shipped', e.target.value)}
                            disabled={updatingOrderId === order._id}
                          >
                            <option value="" disabled>Assign Rider & Ship</option>
                            {riders.map(rider => (
                              <option key={rider._id} value={rider._id}>
                                {rider.user?.name} ({rider.user?.email})
                              </option>
                            ))}
                          </select>
                        </div>
                      ) : order.status === 'shipped' ? (
                        <span className="text-blue-600 font-semibold">Shipped</span>
                      ) : order.status === 'delivered' ? (
                        <span className="text-purple-600 font-semibold">Delivered</span>
                      ) : (
                        <span className="text-gray-500">-</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-4">Rider Management</h2>
        {loading ? (
          <div className="flex justify-center items-center h-16">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#00ffe7]"></div>
          </div>
        ) : (
          <div>
            <ul className="divide-y divide-gray-200">
              {riders.map(rider => (
                <li key={rider._id} className="py-3">
                  <span className="font-semibold">{rider.user?.name}</span> <span className="text-xs text-gray-400">({rider.user?.email})</span>
                  <span className="ml-2 text-sm text-gray-600">Active Orders: {rider.activeOrders?.length || 0}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
} 